// The unified COMMAND layer: every human-initiated mutation of GameState expressed as one serializable
// value, applied through one funnel. This is the whole surface a multiplayer relay has to carry: the
// engine is deterministic, so identical starting state + identical ordered commands = identical state on
// every client (AI turns are recomputed locally, never relayed). Legality is re-checked inside the engine
// functions themselves on EVERY client, so an illegal (tampered) command is rejected everywhere.
// Plain data + pure functions, no DOM, no RNG - safe inside src/game.

import {
  moveOrAttack, startTrain, startMarket, startHarbor, startBarracks, cancelProduction, startResearch, cancelResearch,
  declareWar, makePeace, breakAlliance, proposeAlliance, proposePeace, proposeTrade, executeTrade, tradeTakers,
  respondOffer, runTurnGen, checkGameEnd, rerouteVoyage, type CombatResult, type DiploResult, type VoyageResult,
} from './engine';
import type { FactionId, GameState, RegionId, ResAmt, UnitType } from './types';

export type Command =
  | { kind: 'move'; from: RegionId; to: RegionId }
  | { kind: 'train'; region: RegionId; unitType: UnitType }
  | { kind: 'market'; region: RegionId }
  | { kind: 'harbor'; region: RegionId }
  | { kind: 'barracks'; region: RegionId }
  | { kind: 'reroute'; voyage: number; to: RegionId } // send a STALLED boat to a new beach (lands at once)
  | { kind: 'cancelProduction'; region: RegionId }
  | { kind: 'research'; tech: string }
  | { kind: 'cancelResearch' }
  | { kind: 'declareWar'; to: FactionId }
  | { kind: 'makePeace'; to: FactionId }
  | { kind: 'breakAlliance'; to: FactionId }
  | { kind: 'proposeAlliance'; to: FactionId }
  | { kind: 'trade'; to: FactionId; give: ResAmt; want: ResAmt }
  | { kind: 'respondOffer'; offerId: number; accept: boolean }
  | { kind: 'endTurn' };

export interface CommandResult {
  ok: boolean;
  combat?: CombatResult | null; // for 'move': the full result, so the caller can animate it
  voyage?: VoyageResult | null; // for 'reroute': what the re-aimed crossing did, so the caller can animate it
  diplo?: DiploResult; // for 'proposeAlliance' / a 'trade' that became an offer
  newOffers?: number[]; // for 'endTurn': ids of AI offers raised during the advance (UI toasts them)
}

/** Apply one command for faction `fid`. The single choke point a lockstep layer relays through.
 *  Rules: only a living human faction may act, and in multiplayer only on its own turn ('respondOffer'
 *  is also allowed off-turn so a pending offer can be answered when its owner sees it). */
export function applyCommand(g: GameState, fid: FactionId, cmd: Command): CommandResult {
  const f = g.factions[fid];
  if (!f || !f.isPlayer || f.eliminated || g.phase !== 'playing') return { ok: false };
  if (g.mode === 'mp' && g.turnOf !== fid && cmd.kind !== 'respondOffer') return { ok: false }; // not your turn
  switch (cmd.kind) {
    case 'move': {
      const combat = moveOrAttack(g, cmd.from, cmd.to, fid);
      // a decisive capture (last rival eliminated, or crossing the domination line) must end the game
      // HERE, inside the shared command application, so every lockstep client flips to 'over' at the
      // same seq instead of waiting for its own endTurn advance (checkGameEnd is phase-guarded, so the
      // solo pointerup's second call stays a harmless no-op)
      if (combat) checkGameEnd(g);
      return { ok: !!combat, combat };
    }
    case 'train':
      return { ok: startTrain(g, cmd.region, fid, cmd.unitType) };
    case 'market':
      return { ok: startMarket(g, cmd.region, fid) };
    case 'harbor':
      return { ok: startHarbor(g, cmd.region, fid) };
    case 'barracks':
      return { ok: startBarracks(g, cmd.region, fid) };
    case 'reroute': {
      const voyage = rerouteVoyage(g, fid, cmd.voyage, cmd.to);
      if (voyage) checkGameEnd(g); // a landing can take the last rival's last tile
      return { ok: !!voyage, voyage };
    }
    case 'cancelProduction':
      return { ok: cancelProduction(g, cmd.region, fid) };
    case 'research':
      return { ok: startResearch(g, fid, cmd.tech) };
    case 'cancelResearch':
      return { ok: cancelResearch(g, fid) };
    case 'declareWar':
      return { ok: declareWar(g, fid, cmd.to) };
    case 'makePeace': {
      // a HUMAN victim gets a consent step: peace becomes an offer in their inbox (like alliance/trade),
      // so a losing attacker cannot force peace on them unilaterally; an AI target keeps the direct call
      // (its consent is the cooldown check inside makePeace)
      if (g.factions[cmd.to]?.isPlayer) {
        const diplo = proposePeace(g, fid, cmd.to);
        return { ok: diplo !== 'rejected', diplo };
      }
      return { ok: makePeace(g, fid, cmd.to) };
    }
    case 'breakAlliance':
      breakAlliance(g, fid, cmd.to);
      return { ok: true };
    case 'proposeAlliance': {
      const diplo = proposeAlliance(g, fid, cmd.to);
      return { ok: diplo !== 'rejected', diplo };
    }
    case 'trade': {
      // a HUMAN counterparty gets an offer in their inbox (answered on their own turn); an AI counterparty
      // must genuinely accept the deal (tradeTakers re-checks on every client - no forcing trades on the AI)
      if (g.factions[cmd.to]?.isPlayer) {
        const diplo = proposeTrade(g, fid, cmd.to, cmd.give, cmd.want);
        return { ok: diplo !== 'rejected', diplo };
      }
      if (!tradeTakers(g, fid, cmd.give, cmd.want).includes(cmd.to)) return { ok: false };
      return { ok: executeTrade(g, fid, cmd.to, cmd.give, cmd.want) };
    }
    case 'respondOffer':
      return { ok: respondOffer(g, cmd.offerId, cmd.accept, fid) };
    case 'endTurn': {
      if (g.mode === 'mp' && g.turnOf !== fid) return { ok: false };
      const newOffers: number[] = [];
      const gen = runTurnGen(g);
      for (let s = gen.next(); !s.done; s = gen.next()) newOffers.push(s.value);
      return { ok: true, newOffers };
    }
  }
}
